import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Dashboard from "../../assets/payslips.png";
import Dashboard2 from "../../assets/prime.png";
import Collaborateur from "../../assets/profil.png";
import Presence from "../../assets/heure_supp.png";
import { MdReceipt, MdAttachMoney, MdGroup, MdAccessTime, MdCheck, MdTrendingUp, MdSecurity, MdSpeed } from "react-icons/md";

import { motion, AnimatePresence } from "framer-motion";

const imageDefs = [
  { key: "payslip", img: Dashboard, icon: <MdReceipt size={22} className="mr-2" /> },
  { key: "remuneration", img: Dashboard2, icon: <MdAttachMoney size={22} className="mr-2" /> },
  { key: "collaborators", img: Collaborateur, icon: <MdGroup size={22} className="mr-2" /> },
  { key: "attendance", img: Presence, icon: <MdAccessTime size={22} className="mr-2" /> },
];

const YrosPages = () => {
  const { t } = useTranslation();
  const images = imageDefs.map((def) => ({
    ...def,
    label: t(`yrosPages.items.${def.key}.label`),
    description: t(`yrosPages.items.${def.key}.description`),
    features: t(`yrosPages.items.${def.key}.features`, { returnObjects: true }),
  }));
  const [selected, setSelected] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-[#0c5b6d]/20 to-[#0c5b6d]/30 rounded-full blur-3xl"
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-[#0c5b6d]/20 to-[#0c5b6d]/30 rounded-full blur-3xl"
          animate={{
            x: [0, -80, 0],
            y: [0, 60, 0],
            scale: [1, 0.8, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-gradient-to-r from-[#0c5b6d]/10 to-[#0c5b6d]/20 rounded-full blur-3xl"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </div>

      {/* Floating particles */}
      {[...Array(12)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-blue-400/30 rounded-full"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0.3, 0.8, 0.3],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
            ease: "easeInOut"
          }}
        />
      ))}

      <div className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <motion.div 
            className="text-center mb-16"
            variants={containerVariants}
            initial="hidden"
            animate={isVisible ? "visible" : "hidden"}
          >
            <motion.div
              variants={itemVariants}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-[#0c5b6d]/10 to-[#0c5b6d]/20 backdrop-blur-sm rounded-full border border-[#0c5b6d]/30 mb-6"
            >
              <MdTrendingUp className="w-4 h-4 text-[#0c5b6d] mr-2" />
              <span className="text-sm font-medium text-[#0c5b6d]">{t('yrosPages.badge')}</span>
            </motion.div>

            <motion.h2
              variants={itemVariants}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-gray-900 via-[#0c5b6d] to-[#0c5b6d] bg-clip-text text-transparent mb-6"
            >
              {t('yrosPages.heading')}
              <br />
              <span className="text-[#0c5b6d]">{t('yrosPages.heading_highlight')}</span>
            </motion.h2>

            <motion.p
              variants={itemVariants}
              className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
            >
              {t('yrosPages.subtitle')}
            </motion.p>
          </motion.div>

         

          {/* Navigation Tabs */}
          <motion.div 
            className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-3"
            variants={containerVariants}
            initial="hidden"
            animate={isVisible ? "visible" : "hidden"}
          >
            {images.map((item, idx) => (
              <motion.button
                key={item.label}
                onClick={() => setSelected(idx)}
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.03,
                  y: -3
                }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
                className={`group relative px-6 sm:px-8 py-4 sm:py-5 rounded-2xl cursor-pointer transition-all duration-300 font-medium flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#0c5b6d] focus:ring-offset-2 backdrop-blur-sm border
                  ${selected === idx
                    ? "bg-gradient-to-r from-[#0c5b6d] to-[#0c5b6d]/80 text-white shadow-xl shadow-[#0c5b6d]/25 border-[#0c5b6d]/30"
                    : "bg-white/80 border-white/50 text-gray-700 hover:bg-white hover:shadow-lg hover:border-[#0c5b6d]/20"
                  }`}
                role="tab"
                aria-selected={selected === idx}
              >
                {/* Animated background for active tab */}
                {selected === idx && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-[#0c5b6d] to-[#0c5b6d]/80 rounded-2xl blur opacity-30"
                    layoutId="activeTabGlow"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
                
                {/* Icon with enhanced animation */}
                <motion.div
                  className={`${selected === idx ? "text-white" : "text-gray-500 group-hover:text-[#0c5b6d]"}`}
                  animate={selected === idx ? { 
                    scale: [1, 1.2, 1],
                    rotate: [0, 5, -5, 0]
                  } : { scale: 1, rotate: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  {item.icon}
                </motion.div>
                
                <span className="text-sm sm:text-base whitespace-nowrap relative z-10">
                  {item.label}
                </span>
              </motion.button>
            ))}
          </motion.div>

          {/* Content Area */}
          <motion.div 
            className="backdrop-blur-xl rounded-3xl sm:p-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            {/* Dynamic Title and Description */}
            <AnimatePresence mode="wait">
              <motion.div
                key={selected}
                className="text-center mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                <div className="flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#0c5b6d] to-[#0c5b6d]/80 rounded-xl flex items-center justify-center text-white mr-4">
                    {images[selected].icon}
                  </div>
                  <h3 className="text-3xl sm:text-4xl font-bold text-gray-900">
                    {images[selected].label}
                  </h3>
                </div>
                <p className="text-gray-600 text-lg max-w-3xl mx-auto mb-6">
                  {images[selected].description}
                </p>
                
                {/* Features Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
                  {images[selected].features.map((feature, idx) => (
                    <motion.div
                      key={feature}
                      className="flex items-center justify-center px-2 py-2 bg-gradient-to-r from-[#0c5b6d]/10 to-[#0c5b6d]/20 rounded-xl border border-[#0c5b6d]/20"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.1 }}
                    >
                      <MdCheck className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm font-medium text-gray-700">{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Enhanced Image Display */}
         
                <AnimatePresence mode="wait">
                  <motion.div
                    key={images[selected].img}
                    className="flex relative align-center justify-center p-4 bg-gray-50 rounded-xl"
                   
                  >
                   
                      <img
                        src={images[selected].img}
                        alt={t('yrosPages.screenshot_alt', { label: images[selected].label })}
                        className="w-full max-w-[900px] rounded-2xl relative z-10 shadow-lg"
                        loading="lazy"
                      />
                                            
    
                    
                    
                    
                  </motion.div>
                </AnimatePresence>
              
           

            {/* Enhanced Feature highlights */}
            <motion.div 
              className="mt-10 flex flex-wrap justify-center gap-8 text-sm"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              {[
                { icon: <MdCheck className="w-4 h-4" />, text: t('yrosPages.highlights.intuitive'), color: "from-green-500 to-emerald-500" },
                { icon: <MdTrendingUp className="w-4 h-4" />, text: t('yrosPages.highlights.auto_update'), color: "from-[#0c5b6d] to-[#0c5b6d]/80" },
                { icon: <MdSecurity className="w-4 h-4" />, text: t('yrosPages.highlights.compliant'), color: "from-[#0c5b6d] to-[#0c5b6d]/80" },
                { icon: <MdSpeed className="w-4 h-4" />, text: t('yrosPages.highlights.performance'), color: "from-orange-500 to-red-500" }
              ].map((item, idx) => (
                <motion.div 
                  key={item.text}
                  className="flex items-center gap-3 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full border border-white/50"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className={`w-8 h-8 bg-gradient-to-r ${item.color} rounded-full flex items-center justify-center text-white`}>
                    {item.icon}
                  </div>
                  <span className="font-medium text-gray-700">{item.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default YrosPages;